import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateAccountSettings from "@waytek/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import React, { ChangeEvent, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
};

const mapStateToProps = (state: ApplicationState) => ({
    editingAccount: state.pages.accountSettings.editingAccount,
    emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationWrapper?: InjectableCss;
    userInformationInnerWrapper?: InjectableCss;
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h4",
    },
};

const styles = userInformationStyles;
const password = "*************";

const AccountSettingsUserInformation = ({
    editingAccount,
    useEmailAsUserName,
    changePasswordUrl,
    emailErrorMessage,
    updateAccountSettings,
    cartLoadedTime,
}: Props) => {
    if (!editingAccount) {
        return null;
    }

    const firstNameRef = React.useRef<HTMLInputElement>(null);
    const lastNameRef = React.useRef<HTMLInputElement>(null);

    const fullName = `${editingAccount.firstName} ${editingAccount.lastName}`;
    const emailChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        updateAccountSettings({ email: event.currentTarget.value });
    };

    const label = (
        <>
            <Typography>{translate("Password")}</Typography>
            <Link {...styles.changePasswordLink} href={changePasswordUrl}>
                {translate("Change")}
            </Link>
        </>
    );

    const localOnFirstNameChange = (inputValue: string) => {
        // updateAccountSettings({ firstName: inputValue });
    };

    const localOnLastNameChange = (inputValue: string) => {
        // updateAccountSettings({ lastName: inputValue });
    };

    const {
        value: nameValue,
        changeHandler: nameChangeHandler,
        keyDownHandler: nameKeyDownHandler,
        blurHandler: nameBlurHandler,
    } = useAccessibleSubmit(editingAccount.firstName, localOnFirstNameChange, cartLoadedTime);

    const {
        value: lastNameValue,
        changeHandler: lastNameChangeHandler,
        keyDownHandler: lastNameKeyDownHandler,
        blurHandler: lastNameBlurHandler,
    } = useAccessibleSubmit(editingAccount.lastName, localOnLastNameChange, cartLoadedTime);

    useEffect(() => {
        updateAccountSettings({ firstName: firstNameRef.current?.value });
    }, [firstNameRef.current?.value])

    useEffect(() => {
        updateAccountSettings({ lastName: lastNameRef.current?.value });
    }, [lastNameRef.current?.value])

    return (
        <StyledWrapper {...styles.userInformationWrapper}>
            <Typography {...styles.userInformationTitle}>{translate("User Information")}</Typography>
            <GridContainer {...styles.userInformationInnerWrapper}>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        name="firstName"
                        label={translate("First Name")}
                        required
                        value={nameValue}
                        onChange={nameChangeHandler}
                        onKeyDown={nameKeyDownHandler}
                        onBlur={nameBlurHandler}
                        ref={firstNameRef}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        name="lastName"
                        label={translate("Last Name")}
                        required
                        value={lastNameValue}
                        onChange={lastNameChangeHandler}
                        onKeyDown={lastNameKeyDownHandler}
                        onBlur={lastNameBlurHandler}
                        ref={lastNameRef}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="email"
                        label={translate("Email")}
                        onChange={emailChangeHandler}
                        value={editingAccount.email}
                        data-test-selector="accountSettings_email"
                        required
                        maxLength={256}
                        error={emailErrorMessage}
                        disabled
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField type="password" label={label} disabled value={password} />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation) as any,
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
